import React from 'react';
import { motion } from 'framer-motion';
import { ClipboardIcon } from '@heroicons/react/24/outline';
import { UseFormWatch } from 'react-hook-form';
import { EyeIcon } from 'lucide-react';
import PropertyCard from '../../../components/property/property-card/PropertyCard';
import { Listing } from '../../../components/property/property-card/PropertyCardTypes';
import { User } from '../../../types/userTypes';
import { getRandomUser } from '../../../mocks/users';
import { ListingFormData } from '../types';

interface ListingReviewProps {
  watch: UseFormWatch<ListingFormData>;
  locationInput: string;
  previewUrls: string[];
  setStep: (step: number) => void;
  listingIntent: 'rent' | 'sale';
}

const ListingReview: React.FC<ListingReviewProps> = ({
  watch,
  locationInput,
  previewUrls,
  setStep,
  listingIntent
}) => {
  const randomUser = getRandomUser();

  // Create a preview listing object from form data
  const previewListing: Listing = {
    id: 0, // Temporary ID for preview
    title: watch('title') || 'Your Property Title',
    type: watch('type') || 'Property Type',
    price: Number(watch('price')) || 0,
    size: Number(watch('size')) || 0,
    location: locationInput || 'Property Location',
    bedrooms: Number(watch('bedrooms')) || 0,
    bathrooms: Number(watch('bathrooms')) || 0,
    description: watch('description') || 'Property Description',
    amenities: watch('amenities') || [],
    availableFrom: watch('availableFrom') || new Date(),
    virtualTourUrl: watch('virtualTourUrl') || '',
    berRating: watch('berRating') || 'N/A',
    images: previewUrls,
    videoUrl: watch('videoUrl') || '',
    listingIntent: 'rent',
    // Add any other required fields with fallback values
    propertyOwnerUid: '',
    user: randomUser as User,
    status: 'all',
    dateAdded: '',
    expirationDate: new Date(),
    propertyViews: 0,
    isExternalListing: false
  };

  return (
    <motion.div
      key="singleReview"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="space-y-6"
    >
      {/* Progress indicator */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-green-600 p-4 rounded-r-lg mb-6">
        <p className="text-sm text-green-800">
          <strong>🎉 Final Step (6 of 6):</strong> Review and publish your
          listing! You're seconds away from reaching thousands of Irish tenants
        </p>
      </div>

      <h2 className="text-2xl font-semibold mb-6 flex items-center">
        <ClipboardIcon className="w-6 h-6 mr-2 text-blue-500" />
        Review & Submit
      </h2>

      {/* Preview Card */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center">
          <EyeIcon className="h-6 w-6 text-blue-500 mr-2" />
          Live Preview – This is how renters will see your listing
        </h3>
        <div className="bg-gray-50 p-6 rounded-lg border-2 border-blue-200">
          <PropertyCard
            listing={previewListing}
            isBookmarked={false}
            onBookmarkToggle={() => {}}
            onShare={() => {}}
            isPreview={true}
          />
        </div>
      </div>

      {/* Success metrics */}
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
        <p className="text-sm text-blue-900">
          <strong>✓ Your listing is ready!</strong> Based on your details,
          expect:
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>
              <strong>20-50 views</strong> in the first 48 hours
            </li>
            <li>
              <strong>5-15 inquiries</strong> from qualified Irish renters
            </li>
            <li>
              Average response time: <strong>2 hours</strong>
            </li>
          </ul>
        </p>
      </div>

      <p className="text-gray-700 font-medium">
        📝 Need to make changes? Click "Edit" next to any field below
      </p>

      <div className="space-y-4">
        {[
          { label: 'Property Title', value: watch('title'), step: 1 },
          { label: 'Property Type', value: watch('type'), step: 1 },
          {
            label: 'Listing Intent',
            value: listingIntent === 'rent' ? '🏠 For Rent' : '💰 For Sale',
            step: 1
          },
          { label: 'Price', value: `€${watch('price')}`, step: 2 },
          { label: 'Location', value: locationInput, step: 2 },
          { label: 'Bedrooms', value: watch('bedrooms'), step: 2 },
          { label: 'Bathrooms', value: watch('bathrooms'), step: 2 },
          {
            label: 'Size',
            value: watch('size') ? `${watch('size')} m²` : 'Not specified',
            step: 2
          },
          {
            label: 'Description',
            value: watch('description')
              ? `${(watch('description') || '').substring(0, 100)}...`
              : 'No description',
            step: 3
          },
          {
            label: 'Amenities',
            value: watch('amenities')?.length
              ? `${watch('amenities').length} selected: ${watch('amenities').slice(0, 3).join(', ')}${watch('amenities').length > 3 ? '...' : ''}`
              : 'None selected',
            step: 3
          },
          {
            label: 'Available From',
            value:
              watch('availableFrom')?.toLocaleDateString('en-IE') || 'Not set',
            step: 4
          },
          {
            label: 'BER Rating',
            value: watch('berRating') || 'Not specified',
            step: 4
          },
          {
            label: 'Virtual Tour',
            value: watch('virtualTourUrl') ? '✓ Added' : 'Not included',
            step: 4
          },
          {
            label: 'Photos',
            value: previewUrls.length
              ? `${previewUrls.length} image${previewUrls.length > 1 ? 's' : ''} uploaded`
              : '⚠️ No images - Add photos to increase inquiries!',
            step: 5
          },
          {
            label: 'Video',
            value: watch('videoUrl') ? '✓ Video added' : 'No video (optional)',
            step: 5
          }
        ].map((item, index) => (
          <div
            key={index}
            className="bg-white p-4 rounded-lg flex justify-between items-start shadow-sm border border-gray-200 hover:border-blue-300 transition"
          >
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                {item.label}
              </h3>
              <p className="text-gray-900 mt-1 text-base">{item.value}</p>
            </div>
            <button
              type="button"
              onClick={() => setStep(item.step)}
              className="ml-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
            >
              Edit
            </button>
          </div>
        ))}
      </div>

      {/* Final CTA encouragement */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-400 p-6 rounded-lg mt-8">
        <h3 className="text-lg font-bold text-green-900 mb-2">
          🚀 Ready to Go Live?
        </h3>
        <p className="text-sm text-green-800">
          Click <strong>"Create Listing"</strong> below to publish instantly.
          Your property will appear on RentSmart.ie and be searchable by
          thousands of active renters across Ireland!
        </p>
      </div>
    </motion.div>
  );
};

export default ListingReview;
